import * as React from 'react';
import {Button as ReactAriaButton} from 'react-aria-components';
import {ThemeDarkIcon} from '@discordapp/design/components/Icon/generated/ThemeDarkIcon.web';
import {ThemeLightIcon} from '@discordapp/design/components/Icon/generated/ThemeLightIcon.web';
import {Text} from '@discordapp/design/components/Text/Text.web';
import {useStateFromStores, useStateFromStoresObject} from '@discordapp/flux';

import * as UIActions from '@developers/actions/UIActions';
import * as UserActions from '@developers/actions/UserActionCreators';
import {BuildOverrideMenuItem, useFetchBuildOverride} from '@developers/components/build_override/BuildOverride';
import {useTheme} from '@developers/hooks/useTheme';
import UIStore from '@developers/stores/UIStore';
import UserStore from '@developers/stores/UserStore';
import Avatar from '@developers/uikit/Avatar';
import Button from '@developers/uikit/Button';
import {Dialog, DialogTrigger} from '@developers/uikit/Dialog';
import HamburgerButton from '@developers/uikit/HamburgerButton';
import {Popover} from '@developers/uikit/Popover';
import {getUserAvatarURL} from '@developers/utils/AvatarUtils';
import {hasUniqueUsername} from '@developers/utils/UserUtils';
import AppLogo from './AppLogo';

import {NOOP} from '@developers/Constants';
import {Messages} from '@developers/i18n';
import styles from './AppHeader.module.css';

export const UserMenu = ({children}: {children: React.ReactNode}) => {
  const {user} = useStateFromStoresObject([UserStore], () => ({
    user: UserStore.user,
  }));

  if (user == null) return null;

  return (
    <>
      <div className={styles.userMenuContent}>
        <div>
          <Text variant="text-sm/normal" color="text-primary">
            {Messages.COMMON_LOGGED_IN_HEADER}
          </Text>
          <Text variant="text-md/medium" className={styles.username}>
            {user.username}
          </Text>
          {!hasUniqueUsername(user) && (
            <Text variant="text-md/medium" color="text-normal" tag="span">
              #{user.discriminator}
            </Text>
          )}
        </div>
        <Button
          className={styles.userMenuLogoutButton}
          color={Button.Color.RED}
          height={Button.Height.SHORT}
          look={Button.Look.LINK}
          onClick={() => UserActions.logOut()}>
          {Messages.ACTIONS_LOG_OUT}
        </Button>
      </div>
      {children}
    </>
  );
};

const AppHeader = () => {
  const user = useStateFromStores([UserStore], () => UserStore.user);
  const isMobileMenuOpen = useStateFromStores([UIStore], () => UIStore.isMenuOpen);

  const {theme, toggleTheme} = useTheme();

  React.useEffect(() => {
    UserActions.fetchCachedUser().catch(NOOP);
  }, []);

  const handleHamburgerClick = React.useCallback(() => {
    if (isMobileMenuOpen) {
      UIActions.closeMobileMenu();
    } else {
      UIActions.openMobileMenu();
    }
  }, [isMobileMenuOpen]);
  const buildOverride = useFetchBuildOverride();

  return (
    <header className={styles.wrapper}>
      <HamburgerButton
        onClick={handleHamburgerClick}
        className={styles.mobileMenu}
        accessibilityLabel={Messages.COMMON_TOGGLE_MENU}
      />
      <AppLogo color={buildOverride != null ? 'var(--text-warning)' : undefined} />
      <div className={styles.rightItems}>
        <div className={styles.themeToggleContainer}>
          <button
            onClick={() => toggleTheme()}
            className={styles.themeToggle}
            aria-label={`Toggle to ${theme === 'light' ? 'dark' : 'light'} theme`}>
            {theme === 'light' ? <ThemeLightIcon /> : <ThemeDarkIcon />}
          </button>
        </div>
        {user == null ? null : (
          <DialogTrigger>
            <ReactAriaButton className={styles.userMenuButton} aria-label={Messages.COMMON_USER_MENU}>
              <Avatar src={getUserAvatarURL(user)} />
            </ReactAriaButton>
            <Popover>
              <Dialog>
                <UserMenu>{buildOverride != null ? <BuildOverrideMenuItem override={buildOverride} /> : null}</UserMenu>
              </Dialog>
            </Popover>
          </DialogTrigger>
        )}
      </div>
    </header>
  );
};

export default AppHeader;
